import { Alert, Box, Stack, Typography } from "@mui/material";
import type { ContractLaser, LaseredCondition } from "@/types";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { LaserReviewIcon } from "@/components/ui/mui/LaserReviewIcon";
import { LaseredConditionStateChip } from "./LaseredConditionsEditor";

const NO_DECIMALS = { minimumFractionDigits: 0, maximumFractionDigits: 0 };

/** The confirm's message: Remove clears the whole laser, whatever its type (PO 2026-10-07). */
export const REMOVE_LASER_OUTCOME = "Their claims will revert to the base policy deductible and totals will recompute.";

interface Props {
  /** The straight laser's deductible; undefined when there is none. */
  straightAmount?: number;
  conditions: LaseredCondition[];
  /** A contract laser or exclusion, when set. */
  contract?: Pick<ContractLaser, "is_contract_lasered" | "is_contract_excluded" | "amt_contract_lasered_deductible">;
  /** The claimant has an open deductible review on this policy. */
  reviewPending?: boolean;
}

/**
 * Inside a "remove laser" confirmation: everything the remove clears (the
 * straight laser, each conditional laser with its state, the contract laser or
 * exclusion), and a note when a deductible review is open.
 */
export const RemoveLaserSummary = ({ straightAmount, conditions, contract, reviewPending }: Props) => {
  const hasContract = !!(contract?.is_contract_lasered || contract?.is_contract_excluded);
  const hasItems = straightAmount !== undefined || conditions.length > 0 || hasContract;
  if (!hasItems && !reviewPending) return null;
  return (
    <Box sx={{ mt: 1.5 }} data-testid="remove-laser-summary">
      {hasItems && (
        <>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            This removes:
          </Typography>
          <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
            {straightAmount !== undefined && (
              <Box component="li">
                <Typography variant="body2" component="span">
                  Straight laser ({formatUSD(straightAmount, NO_DECIMALS)})
                </Typography>
              </Box>
            )}
            {conditions.map((c) => (
              <Box component="li" key={c.id}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="body2" component="span">
                    {c.lasered_condition_name}
                    {typeof c.amt_lasered_conditional_deductible === "number"
                      ? ` (${formatUSD(c.amt_lasered_conditional_deductible, NO_DECIMALS)})`
                      : ""}
                  </Typography>
                  <LaseredConditionStateChip condition={c} compact />
                </Stack>
              </Box>
            ))}
            {hasContract && (
              <Box component="li">
                <Typography variant="body2" component="span">
                  {contract?.is_contract_excluded
                    ? "Contract exclusion"
                    : `Contract laser${
                        contract?.amt_contract_lasered_deductible
                          ? ` (${formatUSD(contract.amt_contract_lasered_deductible, NO_DECIMALS)})`
                          : ""
                      }`}
                </Typography>
              </Box>
            )}
          </Stack>
        </>
      )}
      {reviewPending && (
        <Alert
          severity="warning"
          variant="outlined"
          icon={<LaserReviewIcon size={18} />}
          sx={{ mt: 1.5, fontSize: "0.8125rem" }}
          data-testid="remove-laser-review-pending"
        >
          This claimant has an open deductible review. It is re-evaluated after the recompute and may close or change.
        </Alert>
      )}
    </Box>
  );
};
